// Last-three-pages stack for the breadcrumb back button. sessionStorage so a new tab starts empty.
export const NAV_STACK_KEY = 'quralyst:nav-stack';
export const NAV_STACK_MAX = 3;

export interface NavStackEntry {
  to: string;
  label: string;
}

export function readNavStack(): NavStackEntry[] {
  try {
    const raw = sessionStorage.getItem(NAV_STACK_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isEntry).slice(-NAV_STACK_MAX);
  } catch {
    return [];
  }
}

export function writeNavStack(entries: NavStackEntry[]): NavStackEntry[] {
  const next = entries.slice(-NAV_STACK_MAX);
  try {
    sessionStorage.setItem(NAV_STACK_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

export function clearNavStack(): void {
  try {
    sessionStorage.removeItem(NAV_STACK_KEY);
  } catch {
    /* ignore */
  }
}

export function pushNavStack(entry: NavStackEntry): NavStackEntry[] {
  const stack = readNavStack();
  const last = stack[stack.length - 1];
  if (last?.to === entry.to) return stack;
  stack.push(entry);
  return writeNavStack(stack);
}

export function popNavStack(): NavStackEntry | null {
  const stack = readNavStack();
  const target = stack.pop() ?? null;
  writeNavStack(stack);
  return target;
}

/** Jump to a stored page and drop it plus everything after it. */
export function jumpNavStack(index: number): NavStackEntry | null {
  const stack = readNavStack();
  if (index < 0 || index >= stack.length) return null;
  const target = stack[index];
  writeNavStack(stack.slice(0, index));
  return target;
}

function isEntry(value: unknown): value is NavStackEntry {
  if (!value || typeof value !== 'object') return false;
  const rec = value as Record<string, unknown>;
  return typeof rec.to === 'string' && typeof rec.label === 'string';
}
